import { action, computed, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import type {
  FetchMemberFn,
  FetchUserDataFn,
  GetCurrentMemberFn,
  UpdateMemberFn,
  UpdateUserDataFn,
} from '@/features/iam'
import { SearchableUser } from '@/features/iam/users/SearchableUser'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import { type Id } from '@/infra/id'
import { ModalViewModel } from '@/infra/ModalViewModel'

export type CapacityType = 'Global' | 'Workspace'

export class ManageUserCapacityDialogViewModel {
  readonly modal = new ModalViewModel()

  @observable
  private accessor _selectedUserIds = new Set<Id>()

  @observable
  accessor capacityType: CapacityType = 'Workspace'

  @observable
  accessor workspaceCapacity = 0

  @observable
  accessor globalCapacity = 0

  @observable
  accessor workspace = Workspace.none

  @observable
  accessor selectedUser: SearchableUser = SearchableUser.none

  constructor(
    private readonly _onUpdated: (() => void) | undefined,
    private readonly _fetchMember: FetchMemberFn,
    private readonly _fetchUserData: FetchUserDataFn,
    private readonly _updateMember: UpdateMemberFn,
    private readonly _updateUserData: UpdateUserDataFn,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
  ) {}

  @computed
  get selectedUserIds() {
    return Array.from(this._selectedUserIds)
  }

  @computed
  get isPlatformAdmin() {
    return this._getCurrentMember().user.platformAdmin
  }

  @computed
  get canChooseCapacityType() {
    return !!this.workspace.id && this.isPlatformAdmin
  }

  @computed
  get selectedUserName() {
    return this.selectedUser !== SearchableUser.none
      ? this.selectedUser.displayName
      : null
  }

  @action.bound
  setCapacityType(type: CapacityType) {
    this.capacityType = type
  }

  async showWithUser(user: SearchableUser, workspace: Workspace) {
    this._select(new Set([user.publicId]), workspace)
    runInAction(() => {
      this.selectedUser = user
    })

    const withRetry = this._createAlertRetryWrapper('Loading capacity failed')
    const capacities = await withRetry(() =>
      Promise.all([
        workspace.id
          ? this._fetchMember({
              workspaceId: workspace.id,
              userPublicId: user.publicId,
            }).then((member) => member?.maxConcurrentProviderJobs ?? 0)
          : null,
        this.isPlatformAdmin
          ? this._fetchUserData(user.publicId, { skipCache: true }).then(
              (userData) => userData.jobCapacity,
            )
          : null,
      ]),
    )
      // The retry alert has reported the failure.
      .catch(() => null)

    if (!capacities) {
      return
    }

    const [workspaceCapacity, globalCapacity] = capacities

    runInAction(() => {
      if (workspaceCapacity !== null) {
        this.workspaceCapacity = workspaceCapacity
      }
      if (globalCapacity !== null) {
        this.globalCapacity = globalCapacity
      }
      this.modal.show()
    })
  }

  @action.bound
  open(selectedUserIds: Set<Id>, workspace: Workspace) {
    this._select(selectedUserIds, workspace)
    this.modal.show()
  }

  submit = task.resolved(async () => {
    const alert = this._newAlert()
      .withState('pending')
      .saying('Updating Capacity!')
      .autoDismiss()
      .show()

    try {
      await this._updateCapacity()
    } catch (err) {
      alert.dismiss()
      this._createAlertErrorHandler('Updating capacity failed')(err)
    }

    alert
      .withState('success')
      .saying('Success!', 'Capacity updated successfully!')
      .autoDismiss()
      .show()

    this._onUpdated?.()

    this.close()
  })

  @action.bound
  close() {
    this.modal.close()
    this.globalCapacity = 0
    this.workspaceCapacity = 0
    this.selectedUser = SearchableUser.none
    this._selectedUserIds = new Set([SearchableUser.none.publicId])
  }

  @action.bound
  private _select(selectedUserIds: Set<Id>, workspace: Workspace) {
    this._selectedUserIds = selectedUserIds
    this.workspace = workspace
    this.capacityType = workspace === Workspace.none ? 'Global' : 'Workspace'
  }

  private async _updateCapacity() {
    if (this.capacityType === 'Workspace') {
      await Promise.all(
        this.selectedUserIds.map((userPublicId) =>
          this._updateMember({
            workspaceId: this.workspace.id,
            userPublicId,
            maxConcurrentProviderJobs: this.workspaceCapacity,
          }),
        ),
      )
      return
    }

    await Promise.all(
      this.selectedUserIds.map((userPublicId) =>
        this._updateUserData(userPublicId, {
          jobCapacity: this.globalCapacity,
        }),
      ),
    )
  }
}
